import { useCallback, useContext, useEffect, useId, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ReactComponent as EditIcon } from "assets/images/common/edit.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import { LoaderContext } from "context/GlobalLoader";

import Button from "components/Button";
import Card from "components/Card";
import Tabs from "components/Tabs";
import PageTemplate from "modules/common/PageTemplate";
import PortfolioAssetsTab from "modules/portfolios/AssetsTab";
import PortfolioDevicesTab from "modules/portfolios/DevicesTab";
import PortfolioIncidentsTab from "modules/portfolios/IncidentsTab";
import PortfolioInfoTab from "modules/portfolios/InfoTab";
import PortfolioOverviewTab from "modules/portfolios/OverviewTab";
import { PortfolioFormValues } from "widgets/Modal/CreatePortfolio/Content";

import { ModalType } from "entities/common";
import { UpdatePortfolioRequest } from "entities/portfolios";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import {
  getComplianceScoreRequest,
  getPortfolioRequest,
  resetCurrentPortfolio,
  resetSuccessfullyRemovedPortfolio,
  resetSuccessfullyUpdatedPortfolio,
  updatePortfolioRequest,
} from "store/slices/portfolioSlice";
import { showToast } from "store/slices/toastSlice";

import styles from "./style.module.scss";

const TABS = [
  { label: "Overview", value: "overview" },
  { label: "Portfolio info", value: "portfolio-info" },
  { label: "Assets", value: "assets" },
  { label: "Incidents", value: "incidents" },
  { label: "Devices", value: "devices" },
];

const PortfolioDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { [QUERY_PARAMS.TAB]: tabParam } = QueryHelper.getFromQueryString(location.search);
  const [activeTab, setActiveTab] = useState(tabParam || "overview");
  const callbackKey = useId();
  const navigate = useNavigate();

  const [backLink] = useState<string>(
    () => (location.state as { from?: string } | null)?.from || PAGES.PORTFOLIOS_LIST,
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      navigate(
        `${PAGES.PORTFOLIO_DETAIL.replace(":id", id?.toString() || "")}${QueryHelper.getQueryString({
          [QUERY_PARAMS.TAB]: tab,
        })}`,
      );
    },
    [navigate, id],
  );
  const dispatch = useAppDispatch();
  const { handleLoad } = useContext(LoaderContext);
  const {
    iaqDashboard,
    complianceScore,
    isCurrentLoading,
    current: portfolio,
    successfullyUpdatedId,
    successfullyRemovedId,
    isIAQDashboardLoading,
    isComplianceScoreLoading,
    isPortfolioDeviceStatisticLoading,
  } = useAppSelector((state) => state.portfolios);
  const { isListLoading, isStatisticsLoading } = useAppSelector((state) => state.incidents);
  const { isLoading: isDevicesLoading } = useAppSelector((state) => state.devices);
  const canManage = portfolio?.can_manage;

  const handleSavePortfolio = (values?: unknown) => {
    const data = values as PortfolioFormValues;

    const body: UpdatePortfolioRequest = {
      portfolio_name: data.name,
      description: data.description,
      city: data.city,
      area: data.area,
      building_name: data.buildingName,
      unit_number: data.unitNumber,
      street: data.street,
      manager_id: Number(data.managerId),
    };

    const newImages = data.images.filter((img) => Boolean(img.file));

    const remainingExistingIds = new Set(
      data.images
        .filter((img) => !img.file && img.id !== undefined && img.id !== null)
        .map((img) => Number(img.id))
        .filter((n) => !Number.isNaN(n)),
    );
    const removedImageIds = (portfolio?.images ?? [])
      .map((img) => img.id)
      .filter((existingId) => !remainingExistingIds.has(existingId));

    dispatch(updatePortfolioRequest({ id: Number(id), body, newImages, removedImageIds }));
  };

  const handleEditPortfolio = () => {
    setModalCallbacks(callbackKey, { onSave: handleSavePortfolio });

    dispatch(
      showModal({
        modalType: ModalType.CREATE_PORTFOLIO,
        modalProps: {
          mode: "edit",
          portfolioId: Number(id),
          callbackKey,
          initialData: {
            name: portfolio?.name || "",
            companyId: portfolio?.company_id?.toString() || "",
            description: portfolio?.description || "",
            city: portfolio?.city || "",
            area: portfolio?.area || "",
            buildingName: portfolio?.building_name || "",
            unitNumber: portfolio?.unit_number || "",
            street: portfolio?.street || "",
            managerId: portfolio?.manager_id?.toString() || "",
            images: portfolio?.images || [],
          },
        },
      }),
    );
  };

  const handleDeletePortfolio = () => {
    dispatch(
      showModal({
        modalType: ModalType.REMOVE_PORTFOLIO,
        modalProps: {
          id,
          name: portfolio?.name || "",
          assets: portfolio?.asset_count ?? 0,
        },
      }),
    );
  };

  // get portfolio
  useEffect(() => {
    dispatch(getPortfolioRequest(Number(id)));
    dispatch(getComplianceScoreRequest(Number(id)));

    return () => {
      dispatch(resetCurrentPortfolio());
    };
  }, [dispatch, id]);

  // loader
  useEffect(() => {
    handleLoad(
      isCurrentLoading ||
        isComplianceScoreLoading ||
        isIAQDashboardLoading ||
        isListLoading ||
        isStatisticsLoading ||
        isPortfolioDeviceStatisticLoading ||
        isDevicesLoading,
    );

    return () => {
      handleLoad(false);
    };
  }, [
    isCurrentLoading,
    isComplianceScoreLoading,
    isIAQDashboardLoading,
    handleLoad,
    isListLoading,
    isStatisticsLoading,
    isPortfolioDeviceStatisticLoading,
    isDevicesLoading,
  ]);

  // successfully updated portfolio
  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyUpdatedPortfolio());
      dispatch(
        showToast({
          id: "successfully-updated-portfolio",
          message: "The portfolio was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch]);

  // successfully removed portfolio
  useEffect(() => {
    if (successfullyRemovedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyRemovedPortfolio());
      dispatch(
        showToast({
          id: "successfully-removed-portfolio",
          message: "The portfolio was successfully deleted.",
          type: "success",
          duration: 3000,
        }),
      );
      navigate(PAGES.PORTFOLIOS_LIST);
    }
  }, [successfullyRemovedId, dispatch]);

  const address = [
    portfolio?.company_city?.label,
    portfolio?.city,
    portfolio?.area,
    portfolio?.building_name,
    portfolio?.unit_number,
    portfolio?.street,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

  return (
    <PageTemplate
      isBackButton
      backButtonLink={backLink}
      title={portfolio?.name || ""}
      description={address}
      actions={
        <div className={styles.detailActions}>
          {canManage && (
            <Button variant="white" form="circle" onClick={handleEditPortfolio}>
              <EditIcon />
            </Button>
          )}

          {canManage && (
            <Button variant="white" form="circle" onClick={handleDeletePortfolio}>
              <TrashIcon />
            </Button>
          )}
        </div>
      }
      className={styles.detail}
    >
      <Card className={styles.card}>
        <Tabs tabs={TABS} value={activeTab} onChange={handleTabChange} />

        <div className={styles.content}>
          {activeTab === "overview" && (
            <PortfolioOverviewTab
              id={Number(id)}
              complianceScore={complianceScore}
              iaqDashboard={iaqDashboard}
              portfolio={{
                value: portfolio?.id?.toString() || "",
                label: portfolio?.name || "",
              }}
              company={{
                value: portfolio?.company_id?.toString() || "",
                label: portfolio?.company_name || "",
              }}
            />
          )}
          {activeTab === "portfolio-info" && <PortfolioInfoTab portfolio={portfolio} />}
          {activeTab === "assets" && (
            <PortfolioAssetsTab id={Number(id)} companyId={portfolio?.company_id?.toString() || ""} />
          )}
          {activeTab === "incidents" && <PortfolioIncidentsTab id={Number(id)} />}
          {activeTab === "devices" && (
            <PortfolioDevicesTab
              id={Number(id)}
              portfolio={{
                value: portfolio?.id?.toString() || "",
                label: portfolio?.name || "",
              }}
              company={{
                value: portfolio?.company_id?.toString() || "",
                label: portfolio?.company_name || "",
              }}
            />
          )}
        </div>
      </Card>
    </PageTemplate>
  );
};

export default PortfolioDetailPage;
